/**
 * PhotoChecklist — Enforces mandatory photo capture before inspection begins.
 *
 * Shows the required photo shots for the current area (4-wall baseline + area-specific).
 * Each requirement shows a description, angle guidance, and capture status.
 * Blocks progression to inspection items until all required photos are taken.
 */

import { useState, useCallback } from 'react'
import { Camera, CheckCircle, Circle, ChevronDown, ChevronUp, Pencil } from 'lucide-react'
import { PhotoCapture } from './PhotoCapture'
import type { FacilityArea } from '../../lib/services/survey'
import type { PhotoRequirement } from '../../lib/surveyGuide'
import type { PendingPhoto } from '../../lib/offline/surveyStore'
import { blobToDataUrl } from '../../lib/offline/photoPreview'

interface PhotoChecklistProps {
  requirements: PhotoRequirement[]
  visitId: string
  surveyId: string
  area: FacilityArea
  areaLabel: string
  /** Called with photo for annotation review */
  onPhotoCapture: (photo: PendingPhoto, requirementIdx: number) => void
  /** Called when user taps a thumbnail to re-annotate a previously captured photo */
  onReviewPhoto?: (photoId: string, blobUrl: string) => void
  /** Called when all requirements met and user clicks "Proceed" */
  onComplete: () => void
}

interface CapturedShot {
  photoId: string
  requirementIdx: number
  /** iOS-safe base64 data URL for preview thumbnail (not a File-backed blob URL). */
  thumbnailUrl: string | null
}

export function PhotoChecklist({
  requirements,
  visitId,
  surveyId,
  area,
  areaLabel,
  onPhotoCapture,
  onReviewPhoto,
  onComplete,
}: PhotoChecklistProps) {
  const [captured, setCaptured] = useState<CapturedShot[]>([])
  const [expandedIdx, setExpandedIdx] = useState<number>(0)

  const countForReq = useCallback(
    (idx: number) => captured.filter((c) => c.requirementIdx === idx).length,
    [captured],
  )

  const isReqMet = useCallback(
    (idx: number) => countForReq(idx) >= requirements[idx].minCount,
    [countForReq, requirements],
  )

  const allMet = requirements.every((_, idx) => isReqMet(idx))
  const totalCaptured = captured.length
  const totalRequired = requirements.reduce((sum, r) => sum + r.minCount, 0)

  // Thumbnails are now data URLs (iOS-safe), so no unmount revoke needed —
  // there is nothing to leak. Intentionally left without a cleanup effect.

  const handleCapture = useCallback(
    (photo: PendingPhoto, reqIdx: number) => {
      // Insert synchronously so the tile appears immediately, then upgrade
      // thumbnailUrl once the FileReader data URL is ready. iOS can evict a
      // File-backed blob URL between the camera return and the next render,
      // so we never keep a createObjectURL preview in state.
      setCaptured((prev) => [...prev, { photoId: photo.photo_id, requirementIdx: reqIdx, thumbnailUrl: null }])
      if (photo.blob) {
        void blobToDataUrl(photo.blob).then((dataUrl) => {
          setCaptured((prev) => prev.map((c) => (c.photoId === photo.photo_id ? { ...c, thumbnailUrl: dataUrl } : c)))
        }).catch(() => { /* leave as null placeholder */ })
      }
      onPhotoCapture(photo, reqIdx)
      // Auto-advance to next incomplete requirement
      const nextIncomplete = requirements.findIndex((r, i) => i > reqIdx && countForReq(i) < r.minCount)
      if (nextIncomplete !== -1) {
        setExpandedIdx(nextIncomplete)
      }
    },
    [onPhotoCapture, requirements, countForReq],
  )

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="app-surface p-4 space-y-2 border-l-4 border-amber-400">
        <div className="flex items-center gap-2">
          <Camera size={18} className="text-amber-500" />
          <span className="text-sm font-bold text-slate-800">Required Photos — {areaLabel}</span>
        </div>
        <p className="text-xs text-slate-500">
          Capture all required photos before beginning the inspection. Each photo will be available
          for pin annotation after capture.
        </p>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-400 rounded-full transition-all duration-300"
              style={{ width: `${totalRequired > 0 ? (totalCaptured / totalRequired) * 100 : 0}%` }}
            />
          </div>
          <span className="text-xs text-slate-500">{totalCaptured}/{totalRequired}</span>
        </div>
      </div>

      {/* Requirement list */}
      {requirements.map((req, idx) => {
        const met = isReqMet(idx)
        const count = countForReq(idx)
        const isExpanded = expandedIdx === idx

        return (
          <div key={`${req.angle}-${idx}`} className="app-surface overflow-hidden">
            {/* Requirement header */}
            <button
              type="button"
              onClick={() => setExpandedIdx(isExpanded ? -1 : idx)}
              className="w-full p-3 flex items-center gap-3 text-left"
            >
              {met ? (
                <CheckCircle size={18} className="text-emerald-500 flex-shrink-0" />
              ) : (
                <Circle size={18} className="text-slate-300 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${met ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {req.label}
                </p>
                <p className="text-[11px] text-slate-400">
                  {count}/{req.minCount} captured
                  {req.minCount > 1 && ' (multiple shots for full coverage)'}
                </p>
              </div>
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {/* Expanded detail + capture */}
            {isExpanded && (
              <div className="px-3 pb-3 space-y-2 border-t border-slate-100 pt-2">
                <p className="text-xs text-slate-600 leading-relaxed">{req.description}</p>
                <PhotoCapture
                  visitId={visitId}
                  surveyId={surveyId}
                  area={area}
                  onCapture={(photo) => handleCapture(photo, idx)}
                />
                {count > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-semibold text-emerald-600">{count} photo(s) captured</p>
                    <div className="flex flex-wrap gap-2">
                      {captured
                        .filter((c) => c.requirementIdx === idx)
                        .map((shot) => (
                          <button
                            key={shot.photoId}
                            type="button"
                            onClick={() => shot.thumbnailUrl && onReviewPhoto?.(shot.photoId, shot.thumbnailUrl)}
                            className="relative group rounded-lg overflow-hidden border border-slate-200 hover:border-blue-400 transition-colors"
                            title="Tap to annotate"
                          >
                            {shot.thumbnailUrl ? (
                              <img
                                src={shot.thumbnailUrl}
                                alt="Captured"
                                className="w-16 h-16 object-cover"
                              />
                            ) : (
                              <div className="w-16 h-16 bg-slate-100 flex items-center justify-center">
                                <Camera size={16} className="text-slate-400" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              <Pencil size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}

      {/* Proceed button */}
      <button
        type="button"
        onClick={onComplete}
        className={`w-full py-3 rounded-lg text-sm font-bold transition-colors ${
          allMet
            ? 'bg-emerald-500 text-white hover:bg-emerald-600'
            : 'bg-amber-500 text-white hover:bg-amber-600'
        }`}
      >
        {allMet
          ? 'All Photos Captured — Begin Inspection'
          : `Continue Without Photos (${totalRequired - totalCaptured} remaining)`}
      </button>
    </div>
  )
}
